import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

let MID = 0;

// Conversation state + command execution against POST /api/aria/command.
export const useAriaChat = ({ pageName, selection, clearSelection, onNavigate }) => {
  const [tab, setTab] = useState("chat");
  const [turns, setTurns] = useState([
    { id: "boot", role: "aria", text: "Hi Ava — I'm Aria, your copilot. I can surface any expense and run operations from any page. What do you need?" },
  ]);
  const [input, setInput] = useState("");
  const [running, setRunning] = useState(false);
  const [missions, setMissions] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [sessionCommands, setSessionCommands] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [audit, setAudit] = useState(null);
  const bottomRef = useRef(null);

  const scrollDown = () => setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
  const loadWorkflows = () => api.get("/aria/workflows").then((r) => setWorkflows(r.data.workflows)).catch(() => {});
  const loadTasks = () => api.get("/aria/tasks").then((r) => setTasks(r.data.tasks || [])).catch(() => {});

  const onOpen = () => { loadWorkflows(); loadTasks(); };

  useEffect(() => { if (tab === "tasks") loadTasks(); }, [tab]);
  useEffect(() => {
    if (tab === "audit") api.get("/aria/audit").then((r) => setAudit(r.data.items)).catch(() => setAudit([]));
  }, [tab]);

  const runCommand = async (text) => {
    const q = (text ?? input).trim();
    if (!q || running) return;
    setInput("");
    setTab("chat");
    const mid = ++MID;
    const uid = `u-${mid}`;
    const aid = `a-${mid}`;
    setTurns((t) => [...t, { id: uid, role: "user", text: q }, { id: aid, role: "aria", loading: true }]);
    setSessionCommands((c) => [...c, q]);
    setMissions((m) => [{ id: mid, label: q, state: "running", detail: null }, ...m].slice(0, 15));
    setRunning(true);
    scrollDown();
    try {
      const r = await api.post("/aria/command", {
        message: q, context: pageName,
        selection: selection.length ? selection.map(({ type, id, label }) => ({ type, id, label })) : undefined,
      });
      setTurns((t) => t.map((turn) => (turn.id === aid ? { id: aid, role: "aria", text: r.data.reply, cards: r.data.cards } : turn)));
      const action = (r.data.cards || []).find((c) => c.type === "action_result");
      setMissions((m) => m.map((mm) => (mm.id === mid ? { ...mm, state: "done", detail: action ? `${action.label} · ${action.count}` : r.data.navigation ? `Opened ${r.data.navigation.page}` : "Done" } : mm)));
      if (action) {
        window.dispatchEvent(new CustomEvent("aria:acted"));
        if (selection.length) clearSelection();
      }
      if (r.data.navigation) {
        const sp = new URLSearchParams(r.data.navigation.params || {});
        if (r.data.save_view_suggestion) sp.set("suggest_save", "1");
        const qs = sp.toString();
        onNavigate(`${r.data.navigation.page}${qs ? `?${qs}` : ""}`);
      }
      loadTasks();
    } catch {
      setTurns((t) => t.map((turn) => (turn.id === aid ? { id: aid, role: "aria", text: "I hit a snag on that one — try again in a moment." } : turn)));
      setMissions((m) => m.map((mm) => (mm.id === mid ? { ...mm, state: "done", detail: "Failed" } : mm)));
    } finally {
      setRunning(false);
      scrollDown();
    }
  };

  const runWorkflow = async (wf) => {
    if (running) return;
    toast.info(`Running workflow: ${wf.name}`, { description: `${wf.steps.length} steps` });
    for (const step of wf.steps) {
      // eslint-disable-next-line no-await-in-loop
      await runCommand(step);
    }
    toast.success(`Workflow complete: ${wf.name}`);
  };

  const saveWorkflow = async () => {
    const name = saveName.trim();
    if (!name || sessionCommands.length === 0) return;
    try {
      await api.post("/aria/workflows", { name, steps: sessionCommands });
      toast.success("Workflow saved");
      setSaving(false); setSaveName("");
      loadWorkflows();
    } catch {
      toast.error("Could not save workflow");
    }
  };

  const deleteWorkflow = async (id, e) => {
    e.stopPropagation();
    try { await api.delete(`/aria/workflows/${id}`); loadWorkflows(); } catch {}
  };

  return {
    tab, setTab, turns, input, setInput, running, missions, tasks, workflows,
    sessionCommands, saving, setSaving, saveName, setSaveName, audit, bottomRef,
    onOpen, runCommand, runWorkflow, saveWorkflow, deleteWorkflow,
  };
};
